import * as React from "react";
import { useParams } from "react-router-dom";
import * as IdentityPermissionStore from "../identityPermissions/IdentityPermissionStore";
import PermissionBrowser from "../identityPermissions/PermissionBrowser";

const ClientPermissions = ({ user, policies }) => {
    const { id } = useParams();
    const [permissions, setPermissions] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(false);
    const [attempt, setAttempt] = React.useState(0);
    const currentId = React.useRef(id);
    currentId.current = id;
    const canManage = !!user && (user.claims.some(c => c.type === "http://schemas.microsoft.com/ws/2008/06/identity/claims/role" && c.value === "Admin") || policies?.some(p => p.permission === "manageClientPermissions"));

    React.useEffect(() => {
        if (!id || !canManage) return;
        let active = true;
        setLoading(true);
        setError(false);
        IdentityPermissionStore.GetClientPermissions(id).then(({ data }) => {
            if (active) setPermissions(data.map(p => p.permission));
        }).catch(() => {
            if (active) setError(true);
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [id, canManage, attempt]);

    const handlePermissionChange = async (permission: string, assigned: boolean) => {
        if (!canManage) return;
        if (assigned) {
            await IdentityPermissionStore.GiveClientPermission(id, permission);
        } else {
            await IdentityPermissionStore.RevokeClientPermission(id, permission);
        }
        const { data } = await IdentityPermissionStore.GetClientPermissions(id);
        if (currentId.current === id) setPermissions(data.map(p => p.permission));
    };

    if (!canManage || !id) return null;
    return (
        <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Client permissions</h3>
            {loading ? <p role="status" className="py-8 text-center text-gray-500">Loading client permissions…</p> : error ? (
                <div role="alert" className="rounded-lg bg-red-50 p-4 text-sm text-red-800">Could not load client permissions. <button type="button" className="underline" onClick={() => setAttempt(attempt + 1)}>Try again</button></div>
            ) : (
                <PermissionBrowser key={id} assignedPermissions={permissions} canModify={() => canManage} onChange={handlePermissionChange} />
            )}
        </div>
    );
};

export default ClientPermissions;
